import { NgModule } from '@angular/core';
import { RouterModule, Routes } from '@angular/router';

import { MonitoringComponent } from '../Core/components/Monitoring/monitoring.component';
import { MonitoringSearchComponent } from '../Shared/components/MonitoringSearch/monitoring-search.component';
import { MonitoringResultComponent } from '../Shared/components/MonitoringResult/monitoring-result.component';
import { BriefingDeactivateGuard } from './guards/briefing-deactivate.guard';
import { BriefingComponent } from '../Briefings/components/Briefing/briefing.component';
import { MediaSubscriptionComponent } from '../MediaSubscriptions/components/MediaSubscription/media-subscription.component';
import { AlertDeactivateGuard } from './guards/alert-deactivate.guard';
import { AlertComponent } from '../Alerts/components/Alert/alert.component';
import { TopicDeactivateGuard } from './guards/topics-deactivate.guard';
import { HypeComponent } from '../../Common/Layout/components/Hype/hype.component';
import { SubscriptionDashboardComponent } from '../MediaSubscriptions/components/SubscriptionDashboard/subscription-dashboard.component';
import { SubscriptionsDeactivateGuard } from './guards/subscriptions-deactivate.guard';
import { FilterResolver } from '../../Common/Basic/service/filter.resolver';
import { CreateNewActivateGuard } from '../../Common/Misc/services/create-new-activate.guard';
import { BriefingReportComponent } from '../Briefings/components/BriefingReport/briefing.report.component';
import { AlertDashboardComponent } from '../Alerts/components/AlertReportComponent/alert-dashboard.component';
import { NewBriefingComponent } from '../Briefings/components/NewBriefing/new-briefing.component';
import { MonitoringAnalyticsDashboardComponent } from '../Shared/components/MonitoringAnalyticsDashboard/monitoring-analytics-dashboard.component';
import { NewOnlineBriefingComponent } from '../Briefings/components/NewOnlineBriefing/new-online-briefing.component';
import { MonitoringRouteGuard } from './guards/monitoring-route-guard.service';

const monitoringRoutes: Routes = [
    {
        path: 'Monitoring',
        component: MonitoringComponent,
        canActivateChild: [MonitoringRouteGuard],
        children: [
            {
                path: '',
                component: HypeComponent,
                data: {title: 'Monitoring.browserTitle'}
            },
            {
                path: 'search',
                component: MonitoringSearchComponent,
                canDeactivate: [TopicDeactivateGuard],
                data: {title: 'Monitoring.search.browserTitle'}
            },
            {
                path: 'search/update',
                component: MonitoringSearchComponent,
                canDeactivate: [TopicDeactivateGuard]
            },
            {
                // search results
                path: 'search/results',
                component: MonitoringResultComponent,
                canDeactivate: [TopicDeactivateGuard],
                resolve: { filters: FilterResolver }
            },
            {
                path: 'search/dashboard',
                component: MonitoringAnalyticsDashboardComponent,
                canDeactivate: [TopicDeactivateGuard]
            },
            {
                path: 'search/syndicates',
                component: MonitoringResultComponent,
                canDeactivate: [TopicDeactivateGuard],
                resolve: { filters: FilterResolver }
            },
            {
                path: 'topics',
                children: [
                    {
                        path: '',
                        component: HypeComponent,
                        data: {title: 'Monitoring.topics.browserTitle'}
                    },
                    {
                        path: 'topic/:id',
                        component: MonitoringResultComponent,
                        canDeactivate: [TopicDeactivateGuard],
                        resolve: { filters: FilterResolver }
                    },
                    {
                        path: 'topic/:id/dashboard',
                        component: MonitoringAnalyticsDashboardComponent,
                        canDeactivate: [TopicDeactivateGuard]
                    },
                    {
                        path: 'topic/:id/syndicates',
                        component: MonitoringResultComponent,
                        canDeactivate: [TopicDeactivateGuard],
                        resolve: { filters: FilterResolver }
                    },
                ]
            },
            {
                path: 'mentions',
                children: [
                    {
                        // Saved coverage hype page
                        path: '',
                        component: HypeComponent,
                        data: {title: 'Monitoring.savedCoverage.browserTitle'}
                    },
                    {
                        // all saved mentions list
                        path: 'saved',
                        component: MonitoringResultComponent,
                        resolve: { filters: FilterResolver },
                        data: {title: 'Monitoring.allSavedCoverage.browserTitle'}
                    },
                    {
                        path: 'saved/dashboard',
                        component: MonitoringAnalyticsDashboardComponent,
                        resolve: { filters: FilterResolver },
                    },
                    {
                        path: 'saved/syndicates',
                        component: MonitoringResultComponent,
                        resolve: { filters: FilterResolver },
                        data: {title: 'Monitoring.allSavedCoverage.browserTitle'}
                    },
                    {
                        // show mentions saved to specified folder
                        path: 'folder/:id',
                        component: MonitoringResultComponent,
                        resolve: { filters: FilterResolver }
                    },
                    {
                        path: 'folder/:id/dashboard',
                        component: MonitoringAnalyticsDashboardComponent
                    },
                    {
                        path: 'folder/:id/syndicates',
                        component: MonitoringResultComponent,
                        resolve: { filters: FilterResolver }
                    }
                ]
            },
            {
                path: 'subscriptions',
                children: [
                    {
                        path: '',
                        component: HypeComponent,
                        data: {title: 'Monitoring.subscriptions.browserTitle'}
                    },
                    {
                        path: 'dashboard',
                        component: SubscriptionDashboardComponent,
                        data: {title: 'Monitoring.subscriptions.browserTitle.dashboard'}
                    },
                    {
                        path: 'subscription',
                        component: MediaSubscriptionComponent,
                        canActivate: [CreateNewActivateGuard],
                        canDeactivate: [SubscriptionsDeactivateGuard],
                        data: {title: 'Monitoring.subscriptions.browserTitle'}
                    },
                    {
                        path: 'subscription/:id',
                        component: MonitoringResultComponent,
                        canDeactivate: [SubscriptionsDeactivateGuard],
                        resolve: { filters: FilterResolver }
                    },
                    {
                        path: 'subscription/:id/update',
                        component: MediaSubscriptionComponent,
                        canDeactivate: [SubscriptionsDeactivateGuard]
                    },
                    {
                        path: 'subscription/:id/dashboard',
                        component: MonitoringAnalyticsDashboardComponent,
                        canDeactivate: [SubscriptionsDeactivateGuard]
                    },
                    {
                        path: 'subscription/:id/syndicates',
                        component: MonitoringResultComponent,
                        canDeactivate: [SubscriptionsDeactivateGuard],
                        resolve: { filters: FilterResolver }
                    },
                ]
            },
            {
                path: 'briefings',
                children: [
                    {
                        path: '',
                        component: HypeComponent,
                        data: {title: 'Monitoring.briefings.browserTitle'}
                    },
                    {
                        path: 'newBriefing',
                        component: NewBriefingComponent,
                        data: {title: 'Monitoring.amplify.createBriefing'}
                    },
                    {
                        path: 'newCanvas',
                        component: NewOnlineBriefingComponent,
                        data: {title: 'Monitoring.amplify.newCanvas'}
                    },
                    {
                        path: 'briefing',
                        component: BriefingComponent,
                        canActivate: [CreateNewActivateGuard],
                        canDeactivate: [BriefingDeactivateGuard],
                        data: {title: 'Monitoring.briefings.browserTitle'}
                    },
                    {
                        path: 'briefing/:id',
                        component: BriefingComponent,
                        canDeactivate: [BriefingDeactivateGuard]
                    },
                    {
                        path: 'canvas',
                        component: BriefingComponent,
                        canActivate: [CreateNewActivateGuard],
                        canDeactivate: [BriefingDeactivateGuard],
                        data: {title: 'Monitoring.briefings.browserTitle'}
                    },
                    {
                        path: 'canvas/:id',
                        component: BriefingComponent,
                        canDeactivate: [BriefingDeactivateGuard]
                    },
                    {
                        path: 'canvas/:id/report',
                        component: BriefingReportComponent,
                        canDeactivate: [BriefingDeactivateGuard]
                    },
                    {
                        path: 'canvas/:id/:id/report',
                        component: BriefingReportComponent,
                        canDeactivate: [BriefingDeactivateGuard]
                    },
                    {
                        path: 'briefing/:id/report',
                        component: BriefingReportComponent,
                        canDeactivate: [BriefingDeactivateGuard]
                    },
                    {
                        path: 'briefing/:id/:id/report',
                        component: BriefingReportComponent,
                        canDeactivate: [BriefingDeactivateGuard]
                    },
                ]
            },
            {
                path: 'alerts',
                children: [
                    {
                        path: '',
                        component: HypeComponent,
                        data: {title: 'Monitoring.alerts.browserTitle'}
                    },
                    {
                        path: 'alert',
                        component: AlertComponent,
                        canActivate: [CreateNewActivateGuard],
                        canDeactivate: [AlertDeactivateGuard],
                        data: {title: 'Monitoring.alerts.browserTitle'}
                    },
                    {
                        path: 'alert/:id',
                        component: AlertComponent,
                        canDeactivate: [AlertDeactivateGuard]
                    },
                    {
                        path: 'alert/:id/:briefingSentId/report',
                        component: AlertDashboardComponent,
                        canDeactivate: [AlertDeactivateGuard]
                    },
                ]
            },
            {
                path: 'reports',
                children: [
                    {
                        path: '',
                        pathMatch: 'full',
                        redirectTo: '/Report/reports',
                    },
                    {
                        path: 'report/:id',
                        pathMatch: 'full',
                        redirectTo: '/Report/reports/report/:id',
                    },
                    {
                        path: 'shared',
                        pathMatch: 'full',
                        redirectTo: '/Report/reports/shared',
                    },
                    {
                        path: '**',
                        pathMatch: 'full',
                        redirectTo: '/Report/reports',
                    },
                ]
            },
            {
                path: '**',
                component: HypeComponent,
                data: {title: 'Monitoring.browserTitle'}
            }
        ]
    }
];

@NgModule({
    imports: [
        RouterModule.forChild(monitoringRoutes)
    ],
    exports: [
        RouterModule
    ],
    providers: [
        BriefingDeactivateGuard,
        AlertDeactivateGuard,
        TopicDeactivateGuard,
        SubscriptionsDeactivateGuard,
        FilterResolver,
        MonitoringRouteGuard,
    ]
})
export class MonitoringRoutingModule { }
